import React, { FC, MouseEventHandler } from 'react'

import styled from 'styled-components'

import { Colors } from 'common-constants/colors'
import { ContactBadge } from 'components/page/ContactList/ContactBadge'
import { CONTACT_TYPE_USER } from 'components/page/ContactList/ContactList.constants'
import { useContactListContext } from 'components/page/ContactList/hooks/ContactListContext'
import { SwitchTyping } from 'components/page/ContactList/Item/SwitchTyping'
import { LastMessage } from 'components/page/ContactList/LastMessage/LastMessage'
import { Counter } from 'components/presentational/counter/Counter'
import { AddToFavoritesSvg } from 'components/presentational/svg/AddToFavoritesSvg'
import { SmileInCircleSvg } from 'components/presentational/svg/SmileInCircleSvg'
import { ContactItem } from 'reducers/contactList/contactList.types'

import { StyledProfileName } from '../Styled/Contact.styled'

export const ContactContent: FC<{
  item: ContactItem
  onFavorite: (isFavorite: boolean) => void
}> = ({ item, onFavorite }) => {
  const { isEdit } = useContactListContext()

  const {
    onlineStatus: { online: isOnline = false } = {},
    age,
    verifiedPhotos,
    isInFavorite,
    id,
  } = item.anketa!

  const showFavoriteButton =
    !item.anketa!.isBot &&
    !item.anketa!.deleted &&
    item.contactTypeId === CONTACT_TYPE_USER

  const showUnreadReaction =
    item.lastReaction &&
    !item.lastReaction.readAt &&
    item.lastReaction.createdAt > item.lastMessage?.createdTs!

  const handleFavoriteClick: MouseEventHandler<SVGSVGElement> = (event) => {
    event.preventDefault()
    onFavorite(isInFavorite)
  }

  return (
    <ContactContentWrapper>
      {item.autoDeleteDateTs && (
        <ContactBadge
          autoDeleteDate={item.autoDeleteDateTs}
          id={item.anketa!.id}
        />
      )}
      <StyledProfileName
        iconUid={String(id)}
        fullName={item.anketa!.name}
        age={age}
        statusVerification={verifiedPhotos}
        online={isOnline}
        isMute={item.isMutedByMe}
      />
      <SwitchTyping userId={item.anketa!.userId}>
        <LastMessage
          lastMessage={item.lastMessage!}
          profile={item.anketa!}
          lastVisit={item.anketa!.lastVisit}
          spaceTimeLocation={item.anketa!.spaceTimeLocation}
          isInlineMessage={item.autoDeleteDateTs !== null}
        />
      </SwitchTyping>
      {!isEdit && (
        <FavoritesSvgContainer>
          {showFavoriteButton && (
            <AddToFavoritesSvg
              data-name={`is-add-to-favorite-${isInFavorite}-action`}
              ariaRole="button"
              tabIndex={0}
              strokeColor="transparent"
              fill={isInFavorite ? Colors.favouriteActive : Colors.noteIcon}
              onClick={handleFavoriteClick}
            />
          )}

          {showUnreadReaction ? (
            <UnreadContainer data-name="unread-reaction">
              <SmileInCircleSvg color={Colors.warning} />
            </UnreadContainer>
          ) : (
            item.unread > 0 && (
              <UnreadContainer data-name="counter-unread-message">
                <Counter value={item.unread} startAnimation={false} />
              </UnreadContainer>
            )
          )}
        </FavoritesSvgContainer>
      )}
    </ContactContentWrapper>
  )
}

const ContactContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  flex-grow: 1;
  flex-direction: column;
  ${(props) => props.theme.paddingRight}: 20px;
  ${(props) => props.theme.paddingLeft}: 10px;

  min-width: 0;
  box-sizing: border-box;
`

export const FavoritesSvgContainer = styled.div`
  position: absolute;
  ${(props) => props.theme.right}: 0;

  top: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  &:focus,
  &:active {
    outline: none;
  }
`

export const UnreadContainer = styled.div`
  margin-top: 20px;
`
